import React, { useState, useEffect } from 'react'
import InstructorSidebar from '../common/instructorSidebar'
import ReactApexChart from 'react-apexcharts'
import ImageWithBasePath from '../../../core/common/imageWithBasePath'
import { dashboardApi, DashboardData, ChartData } from '../../../api/dashboard'

// Custom Indonesian Rupiah formatter
const formatIndonesianRupiah = (amount: string | number): string => {
  const numericAmount =
    typeof amount === 'string'
      ? parseFloat(amount.replace(/[^\d.-]/g, ''))
      : amount

  if (isNaN(numericAmount)) return 'Rp 0'

  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numericAmount)
}

const InstructorDashboard = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [chartData, setChartData] = useState<ChartData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [chartPeriod, setChartPeriod] = useState<
    'weekly' | 'monthly' | 'yearly'
  >('yearly')

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await dashboardApi.getAdminDashboard()
        setDashboardData(response.data)
        setChartData(response.chart)
      } catch (err) {
        console.error('Error fetching dashboard data:', err)
        setError('Failed to load dashboard data')
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  // Generate chart data based on selected period
  const getChartDataForPeriod = () => {
    if (!chartData) return { categories: [], data: [] }

    switch (chartPeriod) {
      case 'weekly':
        return {
          categories: [
            'Day 1',
            'Day 2',
            'Day 3',
            'Day 4',
            'Day 5',
            'Day 6',
            'Day 7',
          ],
          data: [
            parseFloat(chartData.revenue.weekly.day_1),
            parseFloat(chartData.revenue.weekly.day_2),
            parseFloat(chartData.revenue.weekly.day_3),
            parseFloat(chartData.revenue.weekly.day_4),
            parseFloat(chartData.revenue.weekly.day_5),
            parseFloat(chartData.revenue.weekly.day_6),
            parseFloat(chartData.revenue.weekly.day_7),
          ],
        }
      case 'monthly':
        return {
          categories: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
          data: [
            parseFloat(chartData.revenue.monthly.week_1),
            parseFloat(chartData.revenue.monthly.week_2),
            parseFloat(chartData.revenue.monthly.week_3),
            parseFloat(chartData.revenue.monthly.week_4),
          ],
        }
      case 'yearly':
      default:
        return {
          categories: [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
          ],
          data: [
            parseFloat(chartData.revenue.yearly.january),
            parseFloat(chartData.revenue.yearly.february),
            parseFloat(chartData.revenue.yearly.march),
            parseFloat(chartData.revenue.yearly.april),
            parseFloat(chartData.revenue.yearly.may),
            parseFloat(chartData.revenue.yearly.june),
            parseFloat(chartData.revenue.yearly.july),
            parseFloat(chartData.revenue.yearly.august),
            parseFloat(chartData.revenue.yearly.september),
            parseFloat(chartData.revenue.yearly.october),
            parseFloat(chartData.revenue.yearly.november),
            parseFloat(chartData.revenue.yearly.december),
          ],
        }
    }
  }

  const currentChartData = getChartDataForPeriod()

  // Generate chart configuration with dynamic data
  const getToursChart = () => ({
    chart: {
      height: 290,
      type: 'bar' as const,
      stacked: true,
      toolbar: {
        show: false,
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: 'bottom',
            offsetX: -10,
            offsetY: 0,
          },
        },
      },
    ],
    plotOptions: {
      bar: {
        borderRadius: 5,
        horizontal: false,
        endingShape: 'rounded',
      },
    },
    series: [
      {
        name: 'Earnings',
        data: currentChartData.data,
      },
    ],
    xaxis: {
      categories: currentChartData.categories,
      labels: {
        style: {
          colors: '#4D4D4D',
          fontSize: '13px',
        },
      },
    },
    yaxis: {
      labels: {
        offsetX: -15,
        style: {
          colors: '#4D4D4D',
          fontSize: '13px',
        },
      },
    },
    grid: {
      borderColor: '#4D4D4D',
      strokeDashArray: 5,
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false, // Disable data labels
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: 'linear',
        shadeIntensity: 0.35,
        gradientToColors: ['#00CCDD'], // Second gradient color
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100],
        angle: 90, // This sets the gradient direction from top to bottom
      },
    },
  })

  const toursChart = getToursChart()
  return (
    <>
      <div className="content">
        <div className="container">
          <div className="row">
            {/* Sidebar */}
            <InstructorSidebar />
            {/* /Sidebar */}
            <div className="col-lg-9">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="row">
                <div className="col-md-6 col-xl-4">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <span className="icon-box bg-primary-transparent me-2 me-xxl-3 flex-shrink-0">
                          <ImageWithBasePath
                            src="/assets/img/icon/graduation.svg"
                            alt=""
                          />
                        </span>
                        <div>
                          <span className="d-block">Live Teaching</span>
                          <h4 className="fs-24 mt-1">
                            {loading ? (
                              <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            ) : (
                              dashboardData?.live_teaching || 0
                            )}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 col-xl-4">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <span className="icon-box bg-secondary-transparent me-2 me-xxl-3 flex-shrink-0">
                          <ImageWithBasePath
                            src="/assets/img/icon/book.svg"
                            alt=""
                          />
                        </span>
                        <div>
                          <span className="d-block">CBT</span>
                          <h4 className="fs-24 mt-1">
                            {loading ? (
                              <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            ) : (
                              dashboardData?.cbt || 0
                            )}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 col-xl-4">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <span className="icon-box bg-success-transparent me-2 me-xxl-3 flex-shrink-0">
                          <ImageWithBasePath
                            src="/assets/img/icon/bookmark.svg"
                            alt=""
                          />
                        </span>
                        <div>
                          <span className="d-block">Registrants</span>
                          <h4 className="fs-24 mt-1">
                            {loading ? (
                              <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            ) : (
                              dashboardData?.registrants || '0'
                            )}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 col-xl-4">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <span className="icon-box bg-info-transparent me-2 me-xxl-3 flex-shrink-0">
                          <ImageWithBasePath
                            src="/assets/img/icon/user-octagon.svg"
                            alt=""
                          />
                        </span>
                        <div>
                          <span className="d-block">Users</span>
                          <h4 className="fs-24 mt-1">
                            {loading ? (
                              <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            ) : (
                              dashboardData?.users || 0
                            )}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 col-xl-4">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <span className="icon-box bg-blue-transparent me-2 me-xxl-3 flex-shrink-0">
                          <ImageWithBasePath
                            src="/assets/img/icon/book-2.svg"
                            alt=""
                          />
                        </span>
                        <div>
                          <span className="d-block">Courses</span>
                          <h4 className="fs-24 mt-1">
                            {loading ? (
                              <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            ) : (
                              dashboardData?.courses || 0
                            )}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 col-xl-4">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <span className="icon-box bg-warning-transparent me-2 me-xxl-3 flex-shrink-0">
                          <i className="fa-solid fa-certificate fs-20"></i>
                        </span>
                        <div>
                          <span className="d-block">Certificates</span>
                          <h4 className="fs-24 mt-1">
                            {loading ? (
                              <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            ) : (
                              dashboardData?.certificate || 0
                            )}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 col-xl-4">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <span className="icon-box bg-purple-transparent me-2 me-xxl-3 flex-shrink-0">
                          <ImageWithBasePath
                            src="/assets/img/icon/money-add.svg"
                            alt=""
                          />
                        </span>
                        <div>
                          <span className="d-block">Total Revenue</span>
                          <h4 className="fs-24 mt-1">
                            {loading ? (
                              <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            ) : (
                              formatIndonesianRupiah(
                                dashboardData?.revenue || '0',
                              )
                            )}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center flex-wrap gap-3 justify-content-between border-bottom mb-2 pb-3">
                    <h5 className="fw-bold">
                      Revenue this{' '}
                      {chartPeriod === 'yearly'
                        ? 'Year'
                        : chartPeriod === 'monthly'
                        ? 'Month'
                        : 'Week'}
                    </h5>
                    <div className="d-flex gap-2">
                      <select
                        className="form-select form-select-sm"
                        value={chartPeriod}
                        onChange={(e) =>
                          setChartPeriod(
                            e.target.value as 'weekly' | 'monthly' | 'yearly',
                          )
                        }
                        style={{ width: 'auto' }}
                      >
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                  </div>
                  <div id="earnnings_chart" />

                  {loading ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: '290px' }}
                    >
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <ReactApexChart
                      options={toursChart}
                      series={toursChart.series}
                      type="bar"
                      height={290}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default InstructorDashboard
